import React from 'react';
import { Link } from 'react-router-dom';
import { X, Phone, Mail } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
}

interface Owner {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  secondary_phone?: string;
}

interface Appointment {
  id: string;
  title: string;
  appointment_date: string;
  duration_minutes: number;
  appointment_type: string;
  status: string;
  description?: string;
  notes?: string;
  pets?: Pet[];
  pet_owner?: Owner;
  assigned_vet_user_id?: string;
}

interface TeamMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

interface AwaitingAppointmentModalProps {
  appointment: Appointment;
  teamMembers: TeamMember[];
  onClose: () => void;
}

export const AwaitingAppointmentModal: React.FC<AwaitingAppointmentModalProps> = ({
  appointment,
  teamMembers,
  onClose,
}) => {
  useEscapeKey(onClose, true);

  const formatPhoneNumber = (phone: string) => {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits[0] === '1') {
      // Handle +1 country code
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    // Return original if not standard format
    return phone;
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }),
        time: date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }),
      };
    } catch {
      return { date: 'Invalid date', time: '' };
    }
  };

  const getAssignedVet = () => {
    if (!appointment.assigned_vet_user_id) return null;
    return teamMembers.find(tm => tm.id === appointment.assigned_vet_user_id);
  };

  const { date, time } = formatDateTime(appointment.appointment_date);
  const assignedVet = getAssignedVet();
  const owner = appointment.pet_owner;
  const pets = appointment.pets || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[500] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">
              {appointment.title}
            </h2>
            <p className="text-sm text-gray-500">Awaiting Check-In</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-4"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Left Column - Appointment Details Table */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="border-b border-gray-200 px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Appointment Details
                  </h3>
                </div>
                <div className="overflow-hidden">
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50 w-1/3">
                          Date
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {date}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                          Time
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-lg font-semibold text-blue-600">{time}</div>
                          <div className="text-xs text-gray-500">{appointment.duration_minutes} minutes</div>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                          Type
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 capitalize">
                          {appointment.appointment_type}
                        </td>
                      </tr>
                      {assignedVet && (
                        <tr>
                          <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                            Doctor
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {assignedVet.full_name}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pets */}
              {pets.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="border-b border-gray-200 px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Patient{pets.length > 1 ? 's' : ''}
                    </h3>
                  </div>
                  <div className="p-4 space-y-2">
                    {pets.map((pet) => (
                      <Link
                        key={pet.id}
                        to={`/pets/${pet.id}`}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer group"
                      >
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-sm font-semibold flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                          {pet.name[0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">{pet.name}</div>
                          <div className="text-xs text-gray-500">
                            {pet.species} • {pet.breed || 'Mixed'}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {(appointment.description || appointment.notes) && (
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="border-b border-gray-200 px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Notes
                    </h3>
                  </div>
                  <div className="p-4 space-y-2">
                    {appointment.description && (
                      <p className="text-sm text-gray-600">{appointment.description}</p>
                    )}
                    {appointment.notes && (
                      <p className="text-sm text-gray-500 italic">{appointment.notes}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Owner Contact Table */}
            <div>
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="border-b border-gray-200 px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Owner Information
                  </h3>
                </div>

                {owner ? (
                  <div className="overflow-hidden">
                    <table className="min-w-full">
                      <tbody className="divide-y divide-gray-200">
                        <tr>
                          <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50 w-1/3">
                            Name
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                            {owner.full_name}
                          </td>
                        </tr>
                        {owner.phone && (
                          <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                              Phone
                            </td>
                            <td className="px-4 py-3">
                              <a
                                href={`tel:${owner.phone}`}
                                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                              >
                                {formatPhoneNumber(owner.phone)}
                              </a>
                            </td>
                          </tr>
                        )}
                        {owner.secondary_phone && (
                          <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                              Secondary
                            </td>
                            <td className="px-4 py-3">
                              <a
                                href={`tel:${owner.secondary_phone}`}
                                className="text-blue-600 hover:text-blue-700 text-sm"
                              >
                                {formatPhoneNumber(owner.secondary_phone)}
                              </a>
                            </td>
                          </tr>
                        )}
                        {owner.email && (
                          <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-500 bg-gray-50">
                              Email
                            </td>
                            <td className="px-4 py-3">
                              <a
                                href={`mailto:${owner.email}`}
                                className="text-blue-600 hover:text-blue-700 text-sm break-all"
                              >
                                {owner.email}
                              </a>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Quick Action Buttons */}
                    <div className="p-4 bg-gray-50 border-t border-gray-200 space-y-2">
                      {owner.phone && (
                        <a
                          href={`tel:${owner.phone}`}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          <Phone className="w-4 h-4" />
                          Call {formatPhoneNumber(owner.phone)}
                        </a>
                      )}
                      {owner.email && (
                        <a
                          href={`mailto:${owner.email}`}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
                        >
                          <Mail className="w-4 h-4" />
                          Send Email
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 text-sm text-gray-500">No owner information available</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

